import React, { useState } from 'react';

import { 
  faShare,
  faChevronDown,
  faChevronRight,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Stack,
  Card,
  Title,
  Group,
  Badge,
  Text,
  Button,
  Paper,
  Alert,
  Modal,
  Code
} from '@mantine/core';

import { type Encounter } from './types';
import { useAuth } from '../../contexts/AuthContext';
import { MarkdownRenderer } from '../../utils/markdown';
import { getIntentColor, getWhoColor } from '../../utils/theme';

interface EncounterDetailsProps {
  encounter: Encounter;
  onClose: () => void;
  onRefetch: () => void;
}

const EncounterDetails: React.FC<EncounterDetailsProps> = ({
  encounter,
  onClose,
  onRefetch
}) => {
  const { account } = useAuth();
  const [transcriptsExpanded, setTranscriptsExpanded] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [dangerZoneExpanded, setDangerZoneExpanded] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);

  const handleVoidEncounter = async () => {
    setVoiding(true);
    try {
      const response = await fetch('/api/encounter/void', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: encounter.id }),
      });

      if (!response.ok) {
        throw new Error('Failed to void encounter');
      }

      // Close modal and notify parent
      setVoidModalOpen(false);
      onClose();
      onRefetch();
    } catch (error) {
      console.error('Error voiding encounter:', error);
      // You might want to show an error message to the user here
    } finally {
      setVoiding(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCallerName = (encounter: Encounter) => {
    const caller = encounter._caller;
    if (!caller || (!caller.firstName && !caller.lastName)) {
      return null;
    }
    const parts: string[] = [];
    if (caller.firstName) {
      parts.push(caller.firstName);
    }
    if (caller.lastName) {
      parts.push(caller.lastName);
    }
    if (parts.length === 0) {
      return null;
    }
    return ` (${parts.join(' ').trim()})`;
  };

  const getShareLink = (encounter: Encounter) => {
    const subject = `Insights from Recent Visit with ${encounter._client.firstName}`;
    const body = `Hi ,\n\nWe had a recent visit with ${encounter._client.firstName} around ${formatDate(encounter.createdAt)}. Here are some insights from the visit:\n\n${encounter.insights}\n\nIf you have any questions or need further assistance, please feel free to reach out to us.\n\nRegards,\n${account?.name}`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    return mailtoLink;
  };

  const formatPrompt = (prompt: string) => {
    const lines = prompt.split('\n');
    return lines.map((line) => line.trim()).join('\n').trim();
  };

  return (
    <>
      <Stack gap="md">
        <Card withBorder p="md">
          <Stack gap="sm">
            <Title order={4}>Basic Information</Title>
            <Group gap="md">
              <Badge color={getWhoColor(encounter.who)} variant="filled">
                {encounter.who}
              </Badge>
              <Badge color={getIntentColor(encounter.intent)} variant="filled">
                {encounter.intent}
              </Badge>
            </Group>
            <Text size="sm">
              <strong>Phone:</strong> {encounter.externalPhone}{getCallerName(encounter)}
            </Text>
            <Text size="sm">
              <strong>Date:</strong> {formatDate(encounter.createdAt)}
            </Text>
            <Text size="sm">
              <strong>Agent Phone:</strong> {encounter._agent.phone}
            </Text>
          </Stack>
        </Card>

        {encounter.insights && (
          <Card withBorder p="md">
            <Stack gap="sm">
              <Group align="center" justify="space-between" >
                <Title order={4}>Insights</Title>
                <Button
                  color="blue"
                  component="a"
                  href={getShareLink(encounter)}
                  leftSection={<FontAwesomeIcon icon={faShare} size="sm" />}
                  size="sm"
                  target="_blank"
                  variant="light"
                >
                  Share
                </Button>
              </Group>
              <MarkdownRenderer content={encounter.insights} />
            </Stack>
          </Card>
        )}

        {encounter.transcripts && encounter.transcripts.length > 0 && (
          <Card withBorder p="md">
            <Stack gap="sm">
              <Group align="center" justify="space-between">
                <Title order={4}>Transcript</Title>
                <Button
                  color="gray"
                  leftSection={
                    <FontAwesomeIcon 
                      icon={transcriptsExpanded ? faChevronDown : faChevronRight} 
                      size="sm" 
                    />
                  }
                  size="sm"
                  variant="light"
                  onClick={() => setTranscriptsExpanded(!transcriptsExpanded)}
                >
                  {transcriptsExpanded ? 'Collapse' : 'Expand'}
                </Button>
              </Group>
              {transcriptsExpanded && (
                <Stack gap="xs">
                  {encounter.transcripts.map((transcript, index) => (
                    transcript.message && (
                      <Paper key={index} withBorder p="sm">
                        <Group gap="xs" mb="xs">
                          <Badge 
                            color={transcript.role === 'user' ? 'blue' : 'green'} 
                            size="sm" 
                            variant="light"
                          >
                            {transcript.role}
                          </Badge>
                        </Group>
                        <Text size="sm">{transcript.message}</Text>
                      </Paper>
                    )
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        )}

        {encounter.prompt && (
          <Card withBorder p="md">
            <Stack gap="sm">
              <Group align="center" justify="space-between">
                <Title order={4}>Prompt</Title>
                <Button
                  color="gray"
                  leftSection={
                    <FontAwesomeIcon 
                      icon={promptExpanded ? faChevronDown : faChevronRight} 
                      size="sm" 
                    />
                  }
                  size="sm"
                  variant="light"
                  onClick={() => setPromptExpanded(!promptExpanded)}
                >
                  {promptExpanded ? 'Collapse' : 'Expand'}
                </Button>
              </Group>
              {promptExpanded && (
                <Card withBorder p="sm">
                  <Code block style={{ whiteSpace: 'pre-wrap' }}>{formatPrompt(encounter.prompt)}</Code>
                </Card>
              )}
            </Stack>
          </Card>
        )}

        {/* Void Encounter Section */}
        <Card withBorder p="md" style={{ borderColor: dangerZoneExpanded ? '#ff6b6b' : '#ffb3b3' }}>
          <Stack gap="sm">
            <Group align="center" justify="space-between">
              <Title c={dangerZoneExpanded ? "red" : "#ffb3b3"} order={4}>
                Void Encounter
              </Title>
              <Button
                color="gray"
                leftSection={
                  <FontAwesomeIcon 
                    icon={dangerZoneExpanded ? faChevronDown : faChevronRight} 
                    size="sm" 
                  />
                }
                size="sm"
                variant="light"
                onClick={() => setDangerZoneExpanded(!dangerZoneExpanded)}
              >
                {dangerZoneExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </Group>
            {dangerZoneExpanded && (
              <>
                <Text c="dimmed" size="sm">
                  Permanently void this encounter. This action cannot be undone.
                </Text>
                <Group>
                  <Button
                    color="red"
                    leftSection={<FontAwesomeIcon icon={faTrash} size="sm" />}
                    size="sm"
                    variant="light"
                    onClick={() => setVoidModalOpen(true)}
                  >
                    Void Encounter
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Void Confirmation Modal */}
      <Modal
        centered
        opened={voidModalOpen}
        title="Void Encounter"
        onClose={() => setVoidModalOpen(false)}
      >
        <Stack gap="md">
          <Alert color="red" title="Warning">
            <Text size="sm">
              You are about to void this encounter. This action is <strong>not reversible</strong> and will permanently mark the encounter as voided.
            </Text>
          </Alert>
          
          <Text size="sm">
            Are you sure you want to void this encounter? This will remove it from the encounter history and it will not be used to inform future conversations.
          </Text>
          
          <Group gap="sm" justify="flex-end">
            <Button
              color="gray"
              disabled={voiding}
              variant="light"
              onClick={() => setVoidModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={voiding}
              onClick={() => void handleVoidEncounter()}
            >
              Void Encounter
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default EncounterDetails;

